/**
 * IXVerify v2 — module root (ixv2 WP-2.3; plan §10).
 *
 * Three gates, outermost first:
 *   1. Kill switch (R2 rollback lever): `REACT_APP_IXVERIFY_ENABLED` must be
 *      the string 'true' at build time, else the whole module renders the
 *      platform-standard tool-unavailable screen (the WP-0.2 page). The flag
 *      stays unset in prod until WP-6.1.
 *   2. Session: no session → /login (platform owns identity — v1's parallel
 *      auth stack is gone).
 *   3. Entitlement (R1 mirror): checked ONCE per site via /api/my-tools
 *      (the eReg ProtectedShell pattern); super admins bypass. The backend
 *      re-enforces this on every request regardless.
 *
 * Route structure (WP-3.2): the gate wraps everything; the tabbed Layout
 * wraps the dashboard/roster/studies pages, while the dispense wizard mounts
 * full-screen beside it (R7's one allowed full-screen surface).
 */
import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ToastProvider } from '../ui';
import api from '../services/api';
import IXVerifyUnavailable from '../pages/IXVerifyUnavailable';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Dispense from './pages/Dispense';
import History from './pages/History';
import Subjects from './pages/Subjects';
import Studies from './pages/Studies';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import Accuracy from './pages/Accuracy';
import CheckReview from './pages/CheckReview';

/** Build-time module kill switch (rollback: unset ⇒ tool dark). */
export function isIxvUiEnabled(): boolean {
  return process.env.REACT_APP_IXVERIFY_ENABLED === 'true';
}

// Session + entitlement gate for the whole module — mounted ONCE around the
// nested routes (eReg ProtectedShell pattern; entitlement re-checked when the
// site changes).
function ProtectedShell() {
  const { session, user: authUser, currentSite } = useAuth();
  const navigate = useNavigate();
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const checkEntitlement = async () => {
      // Super admins always have access.
      if (authUser?.role === 'super_admin') {
        if (!cancelled) {
          setHasEntitlement(true);
          setCheckingEntitlement(false);
        }
        return;
      }
      if (!currentSite) {
        // No site selected = no access to site-scoped tools.
        if (!cancelled) {
          setHasEntitlement(false);
          setCheckingEntitlement(false);
        }
        return;
      }
      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const tool = tools.find((t: any) => t.tool_id === 'ixverify');
        if (!cancelled) setHasEntitlement(tool?.is_available === true);
      } catch (error) {
        console.error('Error checking IXVerify entitlement:', error);
        if (!cancelled) setHasEntitlement(false);
      } finally {
        if (!cancelled) setCheckingEntitlement(false);
      }
    };
    if (session) {
      setCheckingEntitlement(true);
      checkEntitlement();
    } else {
      setCheckingEntitlement(false);
    }
    return () => {
      cancelled = true;
    };
  }, [session, authUser?.role, currentSite]);

  if (checkingEntitlement) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md p-8 text-center">
          <h1 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Access denied</h1>
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
            You do not have access to IXVerify for your current site. Please contact your
            administrator if you believe this is an error.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // The gate renders the nested routes; the tabbed Layout is a nested layout
  // route so the dispense wizard can be full-screen (R7's one allowed
  // exception) while staying behind the same session + entitlement checks.
  return <Outlet />;
}

export default function IXVerifyApp() {
  // Kill switch first: while off, the module is the WP-0.2 unavailable page —
  // nothing else mounts, no IXVerify API calls fire (rollback lever).
  if (!isIxvUiEnabled()) {
    return <IXVerifyUnavailable />;
  }

  return (
    <ToastProvider>
      <Routes>
        <Route element={<ProtectedShell />}>
          {/* Full-screen dispense wizard (WP-3.2) — inside the gate, outside the tabbed shell */}
          <Route path="dispense" element={<Dispense />} />
          <Route path="dispense/:id" element={<Dispense />} />
          <Route element={<Layout />}>
            <Route index element={<Dashboard />} />
            {/* WP-3.3 shipped the home at /ixverify/dashboard; the index owns it now. */}
            <Route path="dashboard" element={<Navigate to="/ixverify" replace />} />
            <Route path="history" element={<History />} />
            {/* WP-4.1: the verifier's review page — :id is a check id or a
                session id (the server resolves both). The WP-3.4 bell/push
                deep links land here. */}
            <Route path="checks/:id" element={<CheckReview />} />
            <Route path="subjects" element={<Subjects />} />
            <Route path="studies" element={<Studies />} />
            <Route path="settings" element={<Settings />} />
            {/* Admin-only surfaces (WP-5.2 / WP-4.2) — each page renders a
                denied state for non-admins; the backend 403s regardless. */}
            <Route path="accuracy" element={<Accuracy />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="*" element={<Navigate to="/ixverify" replace />} />
          </Route>
        </Route>
      </Routes>
    </ToastProvider>
  );
}
